import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarClock, ShieldAlert, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err,  setErr]  = useState("");

  useEffect(() => { if (!token) setErr("Missing reset token — check the link in your email."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6)   return toast.error("Use at least 6 characters");
    if (pw !== cf)       return toast.error("Passwords don't match");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      toast.success("Password updated — you can sign in now");
      setDone(true);
      setTimeout(() => nav("/login"), 1200);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to reset password");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen w-full bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-blue-600 grid place-items-center text-white shadow-sm">
            <CalendarClock size={22} />
          </div>
          <div className="mt-3 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Reset your password
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {done ? (
            <div className="text-center space-y-2">
              <Check className="mx-auto text-emerald-600" size={28} />
              <div className="font-medium">Password updated</div>
              <div className="text-sm text-slate-500">Redirecting you to sign in…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-900 flex items-start gap-2">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                <div><b>PLEASE DO NOT use your Sutter password.</b> Pick something unique to this app.</div>
              </div>
              <div>
                <Label htmlFor="np">New password</Label>
                <Input id="np" data-testid="reset-new" type="password" value={pw}
                  onChange={(e) => setPw(e.target.value)} className="mt-1.5" required autoFocus />
                <div className="text-[11px] text-slate-500 mt-1">At least 6 characters.</div>
              </div>
              <div>
                <Label htmlFor="cf">Confirm new password</Label>
                <Input id="cf" data-testid="reset-confirm" type="password" value={cf}
                  onChange={(e) => setCf(e.target.value)} className="mt-1.5" required />
                {cf && pw && cf !== pw && (
                  <div className="text-[11px] text-red-600 mt-1 inline-flex items-center gap-1">
                    <AlertTriangle size={11} /> Passwords don&apos;t match.
                  </div>
                )}
              </div>
              {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
              <Button type="submit" disabled={busy || !token}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
                data-testid="reset-submit">
                {busy ? "Saving…" : "Set new password"}
              </Button>
              <div className="text-center text-xs text-slate-500">
                <Link to="/login" className="hover:underline">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
